import { getBreadcrumbsForPath } from '../publishable'
import { getBrand, getBrandsByMolecule, getMolecule } from '../registry'
import { ComparisonSide, RegistryEntry } from '../types'
import { withDoctorGraph } from './doctorGraph'
import { buildPageGraph, GraphEntity, JsonLdGraph, PageGraphInput } from './graph'

export interface EntryRelations {
  /** Main entity/entities of the entry's page (PRD §49). */
  about: GraphEntity[]
  /** Related entities from the knowledge graph (PRD §30/§31). */
  mentions: GraphEntity[]
}

const resolveSide = ({ kind, slug }: ComparisonSide): GraphEntity | undefined =>
  kind === 'brand' ? getBrand(slug) : getMolecule(slug)

const defined = (entries: Array<GraphEntity | undefined>): GraphEntity[] =>
  entries.filter((entry): entry is GraphEntity => !!entry)

/**
 * Brand ↔ molecule relations of a registry entry, as they must appear in text, links AND JSON-LD (PRD §31):
 * - brand → about the brand, mentions its molecule (Mounjaro → tirzepatida);
 * - molecule → about the molecule, mentions its brands (semaglutida → Wegovy, Ozempic, Poviztra, Semavy, Ozivy);
 * - comparison → about both sides, mentions the molecules of brand sides (molecule is a PRD §29 dimension).
 * A page template must really mention every entity returned here in its visible copy (PRD §42).
 */
export const getEntryRelations = (entry: RegistryEntry): EntryRelations => {
  switch (entry.kind) {
    case 'brand':
      return { about: [entry], mentions: defined([getMolecule(entry.activeIngredient)]) }
    case 'molecule':
      return { about: [entry], mentions: getBrandsByMolecule(entry.slug) }
    case 'comparison': {
      const about = defined([resolveSide(entry.left), resolveSide(entry.right)])
      const molecules = about.map((side) => (side.kind === 'brand' ? getMolecule(side.activeIngredient) : undefined))
      return { about, mentions: defined(molecules).filter((molecule) => !about.includes(molecule)) }
    }
    default:
      return { about: [], mentions: [] }
  }
}

/**
 * Full `@graph` for a registry-driven page, wired from the entry alone: main entity, related entities,
 * review metadata (PRD §44/§50), reviewer Person, breadcrumbs and FAQ. Brand, molecule and comparison
 * pages are typed `MedicalWebPage` (PRD §43). Anything in `overrides` wins over the derived values.
 *
 * Pure builder: it does NOT check publication. Routes must gate on `isPublishable(entry)` before rendering.
 */
export const buildEntryPageGraph = (entry: RegistryEntry, overrides: Partial<PageGraphInput> = {}): JsonLdGraph => {
  const { about, mentions } = getEntryRelations(entry)
  const { review } = entry

  const graph = buildPageGraph({
    about: about.length === 1 ? about[0] : about,
    mentions,
    faq: entry.faq,
    breadcrumbs: getBreadcrumbsForPath(entry.path),
    profileOf: entry.kind === 'doctor' ? entry : undefined,
    ...overrides,
    page: {
      path: entry.path,
      name: entry.title ?? entry.name,
      description: entry.metaDescription,
      medical: entry.kind === 'brand' || entry.kind === 'molecule' || entry.kind === 'comparison',
      datePublished: review.datePublished,
      dateModified: review.dateModified,
      lastReviewed: review.lastReviewed,
      reviewedBy: review.reviewer,
      ...overrides.page
    }
  })
  return entry.kind === 'doctor' ? withDoctorGraph(graph, entry) : graph
}
